/**
 * The minimum shape this predicate needs from a curb event. Deliberately
 * structural rather than tied to `EnforcementIntentDetail`: the notices table
 * (AUT-5763) resolves the same state from `EnforcementIntentSummary`, whose
 * park/doublePark selections are narrower. Typing the input by shape rather
 * than by fragment is what lets both surfaces share this one function instead
 * of growing a second copy of the rule that could drift out of agreement.
 */
export interface LiveSessionEvent {
  startTime: string;
  /** NULL while the event is live — see parkFragment's note on AUT-5502. */
  endTime: string | null;
}

export interface LiveSessionSource {
  park?: LiveSessionEvent | null;
  doublePark?: LiveSessionEvent | null;
}

/**
 * An open session older than this renders as the amber "Unverified" state
 * instead of asserting the vehicle is still present. ~7% of sessions never
 * close observationally (pipeline stoppage / connectivity / occlusion,
 * CV-2333), and those stay open for hours — while the p95 legitimate session
 * is ~98 minutes. Two hours separates the populations with margin.
 */
export const UNVERIFIED_AFTER_MS = 2 * 60 * 60 * 1000;

export type LiveSessionState = { kind: "none" } | { kind: "fresh" } | { kind: "stale"; openHours: number };

/**
 * Derives the still-in-violation badge state from an intent's sessions.
 *
 * The live signal is the backing curb event's end time (park / double park),
 * which the camera pipeline maintains directly. `session.endedAt` is not
 * consulted: it is written back by the session-event-processor worker, which
 * skips intents when more than one matches a session, so it can lag or never
 * arrive — and a session with no readable event row asserts neither presence
 * nor absence, so it contributes nothing rather than a false badge.
 */
export function resolveLiveSessionState(sessions: LiveSessionSource[], nowMs: number): LiveSessionState {
  let earliestOpenStartMs: number | null = null;

  for (const session of sessions) {
    const event = session.park ?? session.doublePark;
    if (!event) {
      continue;
    }
    if (event.endTime != null) {
      continue;
    }
    const startMs = new Date(event.startTime).getTime();
    if (earliestOpenStartMs === null || startMs < earliestOpenStartMs) {
      earliestOpenStartMs = startMs;
    }
  }

  if (earliestOpenStartMs === null) {
    return { kind: "none" };
  }

  const openForMs = nowMs - earliestOpenStartMs;
  if (openForMs > UNVERIFIED_AFTER_MS) {
    return { kind: "stale", openHours: Math.floor(openForMs / (60 * 60 * 1000)) };
  }
  return { kind: "fresh" };
}
